// AnniversaryTimeline — 3-section timeline with central spine (W3.1 §5.1).
//
// Renders 過去 / 現在 / 未來 sections with collapsible future years.
// Past sections never collapse; now section always expanded.
//
// The spine layout is simplified for mobile (single column with a left-side
// vertical line). Desktop uses a 3-col grid `1fr_40px_1fr` for the
// alternating left/right card layout, but we ship the single-column mobile
// version as the primary surface — desktop falls back to single-column too
// for v1 to stay within component-size ceiling.

import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import {
  dedupeSameDay,
  isCollapsedDefault,
  splitYearsBySection,
  type TimelineEventLite,
} from "@/lib/anniversary/timelineSections";
import { TimelineEventCard } from "./TimelineEventCard";
import { usePrefersReducedMotion } from "@/lib/anniversary/countUp";
import type { AnniversaryTimelineResponse } from "@/lib/api/anniversary";
import { cn } from "@/lib/utils/cn";

export type TimelineSectionFilter = "all" | "past" | "now" | "future";

export interface AnniversaryTimelineProps {
  data: AnniversaryTimelineResponse;
  isLoading?: boolean;
  /**
   * W51 (D-4) — which of the three buckets to show.
   *
   * The data was already bucketed three ways (`splitYearsBySection`) before
   * this existed; what was missing was the control, so this is a presentation
   * filter over data already in memory — no refetch, no API change.
   *
   * `"all"` keeps the historical behaviour exactly, including future years
   * collapsed by default.
   */
  section?: TimelineSectionFilter;
}

export function AnniversaryTimeline({
  data,
  isLoading,
  section = "all",
}: AnniversaryTimelineProps) {
  const reduced = usePrefersReducedMotion();
  const allBuckets = useMemo(() => splitYearsBySection(data.years), [data.years]);
  const [userToggled, setUserToggled] = useState<Map<number, boolean>>(new Map());

  // Filtering to one bucket also **expands** it.
  //
  // Future years collapse by default, so 「未來」 rendered as collapsed would
  // look like the filter did nothing: you tapped a filter and got three year
  // headers with nothing under them. An explicit tap still wins — this only
  // changes the *default* for the section you chose to look at.
  const filtering = section !== "all";
  const buckets = useMemo(() => {
    if (!filtering) return allBuckets;
    return { ...allBuckets, [section]: allBuckets[section] };
  }, [allBuckets, section, filtering]);

  const visiblePast = filtering && section !== "past" ? [] : buckets.past;
  const visibleNow = filtering && section !== "now" ? [] : buckets.now;
  const visibleFuture = filtering && section !== "future" ? [] : buckets.future;

  if (isLoading) {
    return (
      <div
        className="px-4 py-3"
        data-testid="anniversary-timeline-loading"
      >
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-md skeleton-shimmer" />
          ))}
        </div>
      </div>
    );
  }

  const toggleYear = (year: number) => {
    setUserToggled((prev) => {
      const next = new Map(prev);
      const cur = next.get(year);
      next.set(year, !(cur ?? false));
      return next;
    });
  };

  const renderYearSection = (
    year: number,
    label: string,
    events: TimelineEventLite[],
    sectionKind: "past" | "now" | "future",
  ) => {
    const sectionRow = data.years.find((s) => s.year === year)!;
    // The prop is shadowed by the lookup above, so the filter flag is read from
    // the closure rather than from `section`.
    const collapsed = filtering
      ? (userToggled.get(year) ?? false)
      : isCollapsedDefault(sectionRow, userToggled);
    const deduped = dedupeSameDay(events);

    return (
      <section
        key={`${sectionKind}-${year}`}
        data-component="YearSection"
        data-section={sectionKind}
        data-collapsed={collapsed ? "true" : undefined}
        data-year={year}
        className="flex flex-col gap-s-4"
      >
        <header
          className={cn(
            "flex items-center gap-2 px-1",
            sectionKind === "now" && "rounded-md bg-shared/5 p-3",
          )}
        >
          <span
            className={cn(
              "font-display tabular-nums leading-none",
              // W52.0 D-4 — one size for all three sections. This read
              // `future ? 40 : 44` on mobile, so the **same screen** set its
              // future years 4px smaller than its past ones. Desktop was 64 for
              // every branch, which is exactly why it survived: the split only
              // exists below `md`. 44 is the majority of the two, and it is the
              // size the timeline's dense middle section already used.
              "text-[44px] md:text-[64px]",
              sectionKind === "now"
                ? "text-shared-strong font-bold"
                : sectionKind === "past"
                  ? "text-ink-2"
                  : "text-ink-3",
            )}
            data-testid="year-label"
          >
            {year}
          </span>
          <span className="text-meta text-ink-3 truncate">{label}</span>
          {sectionKind === "future" ? (
            <button
              type="button"
              onClick={() => toggleYear(year)}
              data-testid={`year-toggle-${year}`}
              aria-expanded={!collapsed}
              aria-label={`${year} 年 ${collapsed ? "展開" : "收合"}`}
              // R0 (W39.0): measured 36×36 — an 8px miss on both axes. The
              // 18px chevron is unchanged; only the target grows, and it grows
              // inwards via a negative margin so the year label's spacing does
              // not shift.
              className="ml-auto inline-flex h-11 w-11 items-center justify-center rounded-full press hover:bg-black/5 -mr-2"
            >
              <ChevronDown
                size={18}
                className={cn(
                  "text-ink-2 transition-transform duration-quick ease-paper",
                  !collapsed && "rotate-180",
                )}
              />
            </button>
          ) : null}
        </header>
        {!collapsed ? (
          <div className="flex flex-col gap-s-3 pl-5 border-l-2 border-hairline md:pl-6">
            {deduped.length === 0 ? (
              <p className="text-meta text-ink-3">這一年沒有事件</p>
            ) : (
              deduped.map((ev) => {
                // Look up the full event from the source year section so we
                // can forward `days_until` + `is_today` + `category` without
                // recomputing them on the client.
                const fullEvent = events.find((e) => e.id === ev.id) ?? ev;
                return (
                  <TimelineEventCard
                    key={ev.id}
                    event={{
                      id: ev.id,
                      kind: ev.kind,
                      title: fullEvent.title ?? ev.id,
                      date: ev.date,
                      category: ev.kind === "anniversary" ? fullEvent.category ?? "custom" : null,
                      days_until: fullEvent.days_until ?? 0,
                      is_today: fullEvent.is_today ?? false,
                      is_anchor: ev.is_anchor,
                      author: null,
                      thumbnail_url: null,
                      // W45.0 `ADR-0114`: without this a milestone has no
                      // reachable destination, because its own `id` is an
                      // `AnniversaryMilestone` primary key. It is rebuilt here
                      // rather than read off `ev` because this literal *is* the
                      // event object — dropping the field would silently return
                      // the bug this replaced.
                      anniversary_id: fullEvent.anniversary_id ?? null,
                    }}
                  />
                );
              })
            )}
          </div>
        ) : null}
      </section>
    );
  };

  return (
    <div
      className="flex flex-col gap-s-6 pb-s-6"
      data-component="AnniversaryTimeline"
    >
      {/* W52.0 D-4 — all three groups are labelled. Only `未來` had a heading,
          so the timeline opened on years with nothing above them and the one
          section that did announce itself was the one you had to scroll to
          reach. The three share one `SectionGroup` rather than three
          hand-written `<h2>`s, so a fourth group cannot ship without a label. */}
      {visiblePast.length > 0 ? (
        <SectionGroup label="過去" testId="timeline-group-past">
          {visiblePast.map((s) =>
            renderYearSection(s.year, s.label, s.events as TimelineEventLite[], "past"),
          )}
        </SectionGroup>
      ) : null}
      {visibleNow.length > 0 ? (
        <SectionGroup label="現在" testId="timeline-group-now">
          {visibleNow.map((s) =>
            renderYearSection(s.year, s.label, s.events as TimelineEventLite[], "now"),
          )}
        </SectionGroup>
      ) : null}
      {visibleFuture.length > 0 ? (
        <SectionGroup label="未來" testId="timeline-group-future">
          {visibleFuture.map((s) =>
            renderYearSection(s.year, s.label, s.events as TimelineEventLite[], "future"),
          )}
        </SectionGroup>
      ) : null}
      {visiblePast.length === 0 &&
      visibleNow.length === 0 &&
      visibleFuture.length === 0 ? (
        <p className="text-meta text-ink-3 text-center py-6" data-testid="timeline-empty">
          {filtering ? "這一段還沒有紀念日" : "還沒有任何紀念日"}
        </p>
      ) : null}
      {/* keep tree-shake-safe refs */}
      <span className="hidden" aria-hidden>
        {reduced ? "reduced" : "full"}
      </span>
    </div>
  );
}

/**
 * W52.0 D-4 — one labelled group, used three times.
 *
 * `gap-s-6` between groups and `gap-s-4` between a group's label and its
 * years is the original `未來` spacing, kept as the single rhythm so the three
 * sections read as three peers rather than one labelled section and two
 * anonymous ones.
 */
function SectionGroup({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-s-4" data-testid={testId} aria-label={label}>
      <h2 className="font-display text-title-3 text-ink-1 px-1">{label}</h2>
      <div className="flex flex-col gap-s-6">{children}</div>
    </section>
  );
}